import { useDispatch, useSelector } from "react-redux"
import { FunnelFilterItem } from "../../types/interfaces"
import { resetFilters, setAllFilterItems, toggleFilterItem } from "../../slices/pricing/pricingSlice"
import { selectFilteredContractTunnels, selectFilters } from "../../slices/pricing/selectors"
import { Checkbox } from "components/forms/fragments/Checkbox"
import { PricingSideMenuCard } from "../common/PricingSideMenuCard"
import { GenericMessage } from "components/feedback/Message"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { NsKey } from "enums/NsKey"

export const FunnelFilters = () => {
  const dispatch = useDispatch()
  const filteredTunnels = useSelector(selectFilteredContractTunnels)
  const filters = useSelector(selectFilters)
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Pricing])

  const handleResetFilters = () => {
    dispatch(resetFilters())
  }

  // Fonction pour gérer le toggle d'un filtre individuel
  const handleToggleFilter = (filterId: string, itemId: string) => {
    dispatch(toggleFilterItem({ filterId, itemId }))
  }

  // Fonction pour vérifier si tous les éléments d'une catégorie sont sélectionnés
  const areAllSelected = (filterItems: FunnelFilterItem[]) => {
    return filterItems.length > 0 && filterItems.every(item => item.value)
  }

  // Fonction pour vérifier si aucun élément d'une catégorie n'est sélectionné
  const areNoneSelected = (filterItems: FunnelFilterItem[]) => {
    return filterItems.every(item => !item.value)
  }

  // Fonction pour gérer la checkbox "Tout sélectionner" d'une catégorie
  const handleCategorySelectAll = (filterId: string, filterItems: FunnelFilterItem[]) => {
    const allSelected = areAllSelected(filterItems)
    dispatch(setAllFilterItems({ filterId, value: !allSelected }))
  }

  // Compter le nombre de filtres actifs
  const getActiveFiltersCount = () => {
    return filters.reduce((count, filterGroup) => {
      return count + filterGroup.items.filter(item => item.value).length
    }, 0)
  }

  const renderFilterItem = (title: string, filterItems: FunnelFilterItem[]) => {
    const allSelected = areAllSelected(filterItems)
    const noneSelected = areNoneSelected(filterItems)
    const someSelected = !allSelected && !noneSelected

    return (
      <div className="bg-[#F2F9FA] flex flex-col gap-2 rounded-xl p-4" key={title}>
        <div className="flex justify-normal gap-2 xl:justify-between mb-1">
          <p className="font-bold text-sm">{title}</p>

          {/* Checkbox pour sélectionner/désélectionner tout */}
          <div className="flex items-center">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => handleCategorySelectAll(title, filterItems)}
            />
          </div>
        </div>

        <div className="flex flex-row justify-between xl:justify-normal xl:flex-col gap-2">
          {filterItems.map((filter) => (
            <div key={filter.id} className="flex justify-between">
              <div className="grow pr-2">
                <p className="font-14 lh-19 font-w400 text-dark">
                  {filter.label}
                </p>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filter.value}
                  onChange={() => handleToggleFilter(title, filter.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const activeFiltersCount = getActiveFiltersCount()

  return (
    <PricingSideMenuCard
      title={t("PRICING.SIDE_MENU.FILTERS")}
      icon="filter-price"
    >
      <div className="relative">
        <div className="flex flex-col gap-4">
          {filters.length === 0 ? (
            <div className="text-center p-4">
              <p className="font-14 text-secondary">{t("PRICING.SIDE_MENU.NO_FILTERS_AVAILABLE")}</p>
            </div>
          ) : (
            filters.map((filterGroup) =>
              renderFilterItem(filterGroup.title, filterGroup.items)
            )
          )}
        </div>

        {/* Message si aucun résultat */}
        {filteredTunnels.length === 0 && activeFiltersCount > 0 && (
          <GenericMessage
            className="mt-4"
            size="sm"
            message={t("PRICING.SIDE_MENU.NO_SELECTED_FILTERS_FOR_FUNNEL")}
            dismissible={false}
            actionButton={
              {
                label: t("COMMON.REINITIALISE"),
                onClick: handleResetFilters
              }
            }
            type="warning"
          />
        )}
      </div>
    </PricingSideMenuCard>

  )
}
